import { Button, Input } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { ArrowLeft, HelpCircle, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useFormCategoriesQuery, useFormsQuery } from "../api/queries";
import { DocumentPortalTour } from "../tour/DocumentPortalTour";
import { formsCenterTourSteps } from "../tour/documentPortalTourSteps";
import { useDocumentPortalTour } from "../tour/useDocumentPortalTour";
import { FormDocumentCard } from "../ui/FormDocumentCard";

const ALL_CATEGORY = { id: "all", label: "ທັງໝົດ" } as const;

function normalizeCategoryParam(value: string | undefined): string {
  const v = (value ?? "all").trim();
  return v === "" ? "all" : v;
}

export function FormsCenterPage() {
  const navigate = useNavigate();
  const tour = useDocumentPortalTour("doc-forms-center-tour-completed");
  const searchParams = useSearch({ from: "/app/documents/forms" }) as {
    category?: string;
    search?: string;
    page?: number;
  };
  const [localSearch, setLocalSearch] = useState(searchParams.search ?? "");

  useEffect(() => {
    setLocalSearch(searchParams.search ?? "");
  }, [searchParams.search]);

  const { data: categoriesData } = useFormCategoriesQuery();
  const categories = [
    ALL_CATEGORY,
    ...(categoriesData?.categories ?? []).map((c) => ({
      id: c.id,
      label: c.name,
    })),
  ];

  const category = normalizeCategoryParam(searchParams.category);
  const page = Math.max(1, searchParams.page ?? 1);
  const routeSearch = {
    category,
    search: searchParams.search || undefined,
  };
  const limit = 6;
  const offset = (page - 1) * limit;

  const query = useFormsQuery({
    category: category === "all" ? undefined : category,
    search: searchParams.search || undefined,
    limit,
    offset,
  });

  const data = query.data?.data ?? [];
  const total = query.data?.meta?.total ?? 0;
  const totalPages = Math.ceil(total / limit);

  const handleCategoryChange = (cat: string) => {
    navigate({
      to: "/app/documents/forms",
      search: { category: cat, search: routeSearch.search, page: 1 },
    });
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    navigate({
      to: "/app/documents/forms",
      search: {
        category: routeSearch.category,
        search: localSearch || undefined,
        page: 1,
      },
    });
  };

  return (
    <>
      <Header />
      <Main>
        <div className="mb-2 flex justify-between space-y-4">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-bold text-2xl tracking-tight">ສູນກາງແບບຟອມ</h1>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-8"
                onClick={tour.startTour}
              >
                <HelpCircle className="size-4" />
              </Button>
            </div>
            <p className="mt-0.5 font-medium text-muted-foreground">
              ແບບຟອມດິຈິຕອລ ຕື່ມອອນໄລນ໌
            </p>
            <div className="mt-2 h-1 w-16 rounded-full bg-black" />
          </div>
          <div>
            <Button
              variant="default"
              className="w-full border-black font-medium hover:bg-black hover:text-white"
              onClick={() => navigate({ to: "/app/dashboard" })}
            >
              <ArrowLeft className="size-4" />
              ກັບຄືນ
            </Button>
          </div>
        </div>

        {/* Filters & search */}
        <div
          data-tourid="doc-forms-filters"
          className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
        >
          <div className="flex flex-wrap gap-2">
            <form onSubmit={handleSearchSubmit} className="flex w-full sm:w-64">
              <div className="relative flex-1">
                <Search className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="ຄົ້ນຫາແບບຟອມ..."
                  value={localSearch}
                  onChange={(e) => setLocalSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
            </form>
            {categories.map((c) => (
              <Button
                key={c.id}
                variant={category === c.id ? "default" : "outline"}
                size="sm"
                className={
                  category === c.id
                    ? "bg-black text-white hover:bg-black/90"
                    : "border-border bg-muted/30 hover:bg-muted/50"
                }
                onClick={() => handleCategoryChange(c.id)}
              >
                {c.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Document grid */}
        <div
          data-tourid="doc-forms-grid"
          className="mb-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3"
        >
          {query.isLoading
            ? Array.from({ length: 6 }, (_, i) => `skeleton-form-${i}`).map(
                (key) => (
                  <div
                    key={key}
                    className="h-48 animate-pulse rounded-xl bg-muted"
                  />
                ),
              )
            : data.map((item) => (
                <FormDocumentCard
                  key={item.id}
                  item={item}
                  onOpenDetail={(formId) =>
                    navigate({
                      to: "/app/documents/forms/$id",
                      params: { id: formId },
                    })
                  }
                />
              ))}
        </div>

        {/* Pagination */}
        <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
          <p className="text-muted-foreground text-sm">
            ສະແດງ {data.length} ຈາກ {total} ເອກະສານ
          </p>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="size-9"
              disabled={page <= 1}
              onClick={() =>
                navigate({
                  to: "/app/documents/forms",
                  search: {
                    category: routeSearch.category,
                    search: routeSearch.search,
                    page: page - 1,
                  },
                })
              }
            >
              ‹
            </Button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const p = i + 1;
              return (
                <Button
                  key={p}
                  variant={page === p ? "default" : "outline"}
                  size="icon"
                  className={`size-9 ${page === p ? "bg-black text-white" : ""}`}
                  onClick={() =>
                    navigate({
                      to: "/app/documents/forms",
                      search: {
                        category: routeSearch.category,
                        search: routeSearch.search,
                        page: p,
                      },
                    })
                  }
                >
                  {p}
                </Button>
              );
            })}
            <Button
              variant="outline"
              size="icon"
              className="size-9"
              disabled={page >= totalPages}
              onClick={() =>
                navigate({
                  to: "/app/documents/forms",
                  search: {
                    category: routeSearch.category,
                    search: routeSearch.search,
                    page: page + 1,
                  },
                })
              }
            >
              ›
            </Button>
          </div>
        </div>
        <DocumentPortalTour
          steps={formsCenterTourSteps}
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
      </Main>
    </>
  );
}
